import { useCallback, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';

const MIN_SCALE = 0.25;
const MAX_SCALE = 8;

export interface ImagePanZoomState {
  scale: number;
  offsetX: number;
  offsetY: number;
  transform: string;
  onWheel: (event: React.WheelEvent) => void;
  onPointerDown: (event: ReactPointerEvent) => void;
  onPointerMove: (event: ReactPointerEvent) => void;
  onPointerUp: (event: ReactPointerEvent) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  zoomFit: () => void;
  reset: () => void;
}

export function useImagePanZoom(enabled: boolean): ImagePanZoomState {
  const [scale, setScale] = useState(1);
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const draggingRef = useRef(false);
  const lastPointerRef = useRef({ x: 0, y: 0 });

  const reset = useCallback(() => {
    setScale(1);
    setOffsetX(0);
    setOffsetY(0);
  }, []);

  const zoomIn = useCallback(() => {
    setScale((value) => Math.min(value * 1.25, MAX_SCALE));
  }, []);

  const zoomOut = useCallback(() => {
    setScale((value) => Math.max(value / 1.25, MIN_SCALE));
  }, []);

  const zoomFit = useCallback(() => {
    reset();
  }, [reset]);

  const onWheel = useCallback(
    (event: React.WheelEvent) => {
      if (!enabled) return;
      event.preventDefault();
      const delta = event.deltaY > 0 ? 0.9 : 1.1;
      setScale((value) => Math.min(Math.max(value * delta, MIN_SCALE), MAX_SCALE));
    },
    [enabled]
  );

  const onPointerDown = useCallback(
    (event: ReactPointerEvent) => {
      if (!enabled) return;
      draggingRef.current = true;
      lastPointerRef.current = { x: event.clientX, y: event.clientY };
      (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    },
    [enabled]
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent) => {
      if (!enabled || !draggingRef.current) return;
      const dx = event.clientX - lastPointerRef.current.x;
      const dy = event.clientY - lastPointerRef.current.y;
      lastPointerRef.current = { x: event.clientX, y: event.clientY };
      setOffsetX((value) => value + dx);
      setOffsetY((value) => value + dy);
    },
    [enabled]
  );

  const onPointerUp = useCallback((event: ReactPointerEvent) => {
    draggingRef.current = false;
    try {
      (event.currentTarget as HTMLElement).releasePointerCapture(event.pointerId);
    } catch {
      /* ignore */
    }
  }, []);

  return {
    scale,
    offsetX,
    offsetY,
    transform: `translate(${offsetX}px, ${offsetY}px) scale(${scale})`,
    onWheel,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    zoomIn,
    zoomOut,
    zoomFit,
    reset
  };
}
